import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from './AuthContext';
import { parseUploadedExcel } from '../utils/billExcelHelper';

const BillContext = createContext();

export const DEFAULT_LETTERHEAD_SETTINGS = {
  enabled: true,
  mode: 'digital', // 'digital' | 'preprinted' | 'none'
  topMarginMm: 38,
  bottomMarginMm: 26,
  sideMarginMm: 8,
  showWatermark: true,
  opacity: 100,
};

// 1. SRFDCL Riverfront Security Format (Standard Reference)
export const DEFAULT_BILL_CONFIG = {
  formatType: 'standard',
  recipient: {
    toTitle: 'TO,',
    designation: 'THE GENERAL MANAGER',
    companyName: 'Sabarmati River Front Development Corporation Limited',
    addressLines: [
      '2nd floor Riverfront house,B/H. H.K. Arts College,',
      'Between Gandhi and Nehru Bridge,',
      'Pujya Pramukh Swami Maharaj Marg river front -west',
      'Ahmedabad -380009',
    ],
    gstin: '24AACCS1188C1ZP',
  },
  invoiceMeta: {
    invoicePrefix: 'RSS',
    invoiceNo: 'RSS/00/2026-27',
    invoiceDate: '03/06/2026',
    sacCode: '998529',
    workOrderNo: 'S.R.F.D.C.L.819',
    workOrderDate: '03-10-2024',
  },
  billingMonthTitle: 'BILL FOR THE MONTH OF MARCH-2026',
  projectScope: {
    code: 'A',
    description:
      'Providing round the clock security service at the sabarmati river front Project area (east side) at Ahmedabad.',
  },
  items: [
    {
      id: '1',
      srNo: 1,
      particular: 'SECURITY OFFICER',
      ratePerDay: 607.4,
      mandays: 30,
      totalAmount: 18222.0,
    },
    {
      id: '2',
      srNo: 2,
      particular: 'SECURITY SUPERVISOR',
      ratePerDay: 593.45,
      mandays: 100,
      totalAmount: 59345.0,
    },
    {
      id: '3',
      srNo: 3,
      particular: 'SECURITY GUARD',
      ratePerDay: 581.83,
      mandays: 3000,
      totalAmount: 1745490.0,
    },
  ],
  penalties: [
    {
      id: 'pen-1',
      label: 'Penalty(-)',
      amount: 100000.0,
    },
  ],
  additionalCharges: [
    {
      id: 'add-1',
      label: 'Suv like Sumo/Bolero',
      amount: 9238.0,
    },
  ],
  serviceCharge: {
    isEnabled: true,
    ratePercentage: 1.01,
    label: 'SERVICE CHARGE @1.01%',
    amount: 17496.18,
  },
  rcmNotice: {
    isEnabled: true,
    text:
      'As Per Government Notification No. 29/2018- Central Tax (Rate) Dated 31-12-2018 RCM (Reverse Charge Mechanism) Introduced Effective From 01-01-2019. Hence, GST Liability Is Of Service Are Recipient, Now.',
    amount: 0,
  },
  companyTaxInfo: {
    gstin: '24AAOFR9854P1Z4',
    panNo: 'AAOFR9854P',
  },
  bankDetails: {
    bankName: 'State Bank of India',
    branchName: '11789, Thakkarbapanagar, Ahmedabad',
    accountNo: '33850191434',
    ifscCode: 'SBIN0011789',
  },
  notes: [
    '1 All Calculations are on the basis of an English calendar months.',
    '2 Subject to Ahmedabad Jurisdiction.',
  ],
  signatory: {
    companyName: 'For, Rajput Security service',
    signatoryTitle: 'Authorised Signatory',
  },
  footer: {
    leftNotice: 'E & O.E.',
    disclaimer: "This is a computer-generated invoice doesn't require any signature.",
  },
  letterhead: {
    enabled: true,
    mode: 'digital',
    topMarginMm: 38,
    bottomMarginMm: 26,
    sideMarginMm: 8,
    showWatermark: true,
    opacity: 100,
  },
  tableSettings: {
    maxLines: 6,
    fillEmptyLines: false,
    density: 'auto', // 'auto' | 'standard' | 'compact' | 'ultra_compact' | 'micro' | 'relaxed'
  },
};

// 2. Dental College Security (Ver 2.0) - INVOICE.xlsx
export const DENTAL_COLLEGE_CONFIG = {
  formatType: 'security_v2',
  recipient: {
    toTitle: 'To,',
    designation: 'THE DEAN',
    companyName: 'Dental Collage',
    addressLines: [
      'Nr.Anupam Cinema',
      'Khokhara,',
      'Ahmedabad',
    ],
    gstin: '24AACTA3101G1ZJ',
  },
  invoiceMeta: {
    invoicePrefix: 'RSS',
    invoiceNo: 'RSS/185/2026-27',
    invoiceDate: '05/08/2026',
    sacCode: '998525',
    workOrderNo: 'OUT WORD NO. B-682',
    workOrderDate: '21/03/2025',
    tenderNo: '115/2024-25',
    tenderIdNo: '124733',
    billingMonthTitle: 'BILL FOR THE MONTH OF JULY-2026',
  },
  billingMonthTitle: 'BILL FOR THE MONTH OF JULY-2026',
  items: [
    {
      id: 'dc-1',
      srNo: 1,
      particular:
        'Providing Security Service (VERSION 2.0) – Office / Commercial / Institutions / Residential; at dental college campus, Ahmedabad.\nFrom 01-07-2026 To 31-07-2026.\nUnarmed Security Guard (MALE/FEMALE)',
      quantity: 40,
      mandays: 1237,
      ratePerDay: 664.09,
      totalAmount: 821479.33,
    },
  ],
  serviceCharge: {
    isEnabled: true,
    ratePercentage: 3.85,
    label: 'Service Charge 3.85%',
    amount: 31626.95,
  },
  taxes: {
    sgstPercentage: 9,
    sgstAmount: 0,
    cgstPercentage: 9,
    cgstAmount: 0,
  },
  roundOff: 0.28,
  roundOffAction: 'subtract',
  rcmNotice: {
    isEnabled: true,
    text:
      'As Per Government Notification No. 29/2018- Central Tax (Rate) Dated 31-12-2018 RCM (Reverse Charge Mechanism) Introduced Effective From 01-01-2019. Hence, GST Liability Is Of Service Are Recipient, Now.',
    amount: 0,
  },
  wageBreakdown: {
    showBreakdown: true,
    title: 'NEW RATE APR-26',
    basic: 512.5,
    pfPercentage: 13,
    pfAmount: 66.63,
    esicPercentage: 3.25,
    esicAmount: 16.66,
    bonusPercentage: 8.33,
    bonusAmount: 42.69,
    leavePercentage: 5,
    leaveAmount: 25.63,
    totalDailyRate: 664.09,
  },
  companyTaxInfo: {
    gstin: '24AAOFR9854P1Z4',
    panNo: 'AAOFR9854P',
  },
  bankDetails: {
    bankName: 'State Bank Of India',
    branchName: 'THAKKARBAPA NAGAR',
    accountNo: '33850191434',
    ifscCode: 'SBIN0011789',
  },
  notes: [
    '1 Subject to Ahmedabad Jurisdiction.',
    '2 This bill is to be paid by 7th of currents months.',
    '3 All Calculations are on the basis of an English calendar months.',
  ],
  signatory: {
    companyName: 'For, Rajput Security service',
    signatoryTitle: 'Authorised Signatory',
  },
  footer: {
    leftNotice: 'E & O.E.',
    disclaimer: "This is a computer-generated invoice doesn't require any signature.",
  },
  letterhead: {
    enabled: true,
    mode: 'digital',
    topMarginMm: 38,
    bottomMarginMm: 26,
    sideMarginMm: 8,
    showWatermark: true,
    opacity: 100,
  },
  tableSettings: {
    maxLines: 6,
    fillEmptyLines: false,
    density: 'auto',
  },
};

// 3. AMC MET Nursing College (Security & Bouncer) - JULY-26.xlsx
export const AMC_NURSING_CONFIG = {
  formatType: 'security_v2',
  recipient: {
    toTitle: 'To,',
    designation: 'THE PRINCIPAL',
    companyName: 'AMC MET NURSING COLLEGE',
    addressLines: [
      'V.S. HOSPITAL CAMPUS',
      'ELLISBRIDGE',
      'AHMEDABAD-380006',
    ],
    gstin: '24AACTA3101G1ZJ',
  },
  invoiceMeta: {
    invoicePrefix: 'RSS',
    invoiceNo: 'RSS/186/2026-27',
    invoiceDate: '06/08/2026',
    sacCode: '998525',
    workOrderNo: 'OUT WORD NO. 529',
    workOrderDate: '27/03/2025',
    tenderNo: '115/2024-25',
    tenderIdNo: '124733',
    billingMonthTitle: 'BILL FOR THE MONTH OF JULY-2026',
  },
  billingMonthTitle: 'BILL FOR THE MONTH OF JULY-2026',
  items: [
    {
      id: 'amc-1',
      srNo: 1,
      particular:
        'Providing Security Service (VERSION 2.0) – Office / Commercial / Institutions / Residential; at AMC MET NURSING COLLEGE AHMEDABAD.\nFrom 01-07-2026 To 31-07-2026.\nUnarmed Security Guard (MALE/FEMALE)',
      quantity: 12,
      mandays: 372,
      ratePerDay: 664.09,
      totalAmount: 247041.48,
    },
    {
      id: 'amc-2',
      srNo: 2,
      particular: 'Bouncer',
      quantity: 1,
      mandays: 31,
      ratePerDay: 692.6,
      totalAmount: 21470.6,
    },
  ],
  serviceCharge: {
    isEnabled: true,
    ratePercentage: 3.85,
    label: 'Service Charge 3.85%',
    amount: 10337.72,
  },
  taxes: {
    sgstPercentage: 9,
    sgstAmount: 0,
    cgstPercentage: 9,
    cgstAmount: 0,
  },
  roundOff: 0.2,
  roundOffAction: 'add',
  rcmNotice: {
    isEnabled: true,
    text:
      'As Per Government Notification No. 29/2018- Central Tax (Rate) Dated 31-12-2018 RCM (Reverse Charge Mechanism) Introduced Effective From 01-01-2019. Hence, GST Liability Is Of Service Are Recipient, Now.',
    amount: 0,
  },
  wageBreakdown: {
    showBreakdown: true,
    title: 'NEW RATE APR-26',
    basic: 512.5,
    pfPercentage: 13,
    pfAmount: 66.63,
    esicPercentage: 3.25,
    esicAmount: 16.66,
    bonusPercentage: 8.33,
    bonusAmount: 42.69,
    leavePercentage: 5,
    leaveAmount: 25.63,
    totalDailyRate: 664.09,
  },
  companyTaxInfo: {
    gstin: '24AAOFR9854P1Z4',
    panNo: 'AAOFR9854P',
  },
  bankDetails: {
    bankName: 'THE KARNAVATI CO OP BANK LTD',
    branchName: 'NIKOL ROAD',
    accountNo: '124003012000007',
    ifscCode: 'GSCB0UTKCBL',
  },
  notes: [
    '1 Subject to Ahmedabad Jurisdiction.',
    '2 This bill is to be paid by 7th of currents months.',
    '3 All Calculations are on the basis of an English calendar months.',
  ],
  signatory: {
    companyName: 'For, Rajput Security service',
    signatoryTitle: 'Authorised Signatory',
  },
  footer: {
    leftNotice: 'E & O.E.',
    disclaimer: "This is a computer-generated invoice doesn't require any signature.",
  },
  letterhead: {
    enabled: true,
    mode: 'digital',
    topMarginMm: 38,
    bottomMarginMm: 26,
    sideMarginMm: 8,
    showWatermark: true,
    opacity: 100,
  },
  tableSettings: {
    maxLines: 6,
    fillEmptyLines: false,
    density: 'auto',
  },
};

// 4. BAOU Open University Sanitation & Facility - INVOICE - Copy.xlsm
export const BAOU_CONFIG = {
  formatType: 'baou_gem',
  recipient: {
    toTitle: 'TO,',
    designation: '',
    companyName: 'DR. BABASAHEB AMBEDAKAR OPEN UNIVERSITY',
    addressLines: [
      'Jyotirmay parisar,',
      'S.G. Highway, Chharodi,',
      'Ahmedabad,',
      'Gujarat-382481',
    ],
    gstin: '24ABJFA1919Q1Z4',
  },
  invoiceMeta: {
    invoicePrefix: 'RSS',
    invoiceNo: 'RSS/183/2026-27',
    invoiceDate: '04/08/2026',
    gemContractNo: 'GEMC-511687737033144',
    generatedDate: '08/12/2025',
    bidRaPbpNo: 'GEM/2025/B/6861369',
    workOrderNo: 'નં. બાઆંઓયૂ/રજી/સ્ટોર્સ/3326/2025  તા.-10/12/2025',
    sacCode: '998539',
    billingMonthTitle: 'Bill for the month of JULY- 2026',
    billingPeriod: '01/07/2026 TO 31/07/2026',
  },
  billingMonthTitle: 'Bill for the month of JULY- 2026',
  projectScope: {
    code: '',
    description:
      'The provision of sanitation and facility management services at "Dr. Babasaheb Ambedkar Open University." TOTAL MANDAYS PER DAYS = 25',
  },
  items: [
    {
      id: 'baou-1',
      srNo: 1,
      particular: 'TOTAL',
      ratePerDay: 692.71,
      mandays: 730.5,
      totalAmount: 506024.66,
    },
  ],
  serviceCharge: {
    isEnabled: true,
    ratePercentage: 3.85,
    label: 'Service charge @ 3.85%',
    amount: 19481.95,
  },
  taxes: {
    cgstPercentage: 9,
    cgstAmount: 45542.22,
    sgstPercentage: 9,
    sgstAmount: 45542.22,
  },
  facilityCharges: [
    {
      id: 'fc-1',
      label:
        'Consumables/Equipments and cleaning agents to be provided by Service Provider',
      amount: 0.01,
    },
    {
      id: 'fc-2',
      label: 'Machineries to be provider by service provider',
      amount: 0.01,
    },
  ],
  extraSalaryRemark: 'EXTRA SALARY 452*12%=54.24 PROVIDED BY BAOU',
  roundOff: -0.07,
  companyTaxInfo: {
    gstin: '24AAOFR9854P1Z4',
    panNo: 'AAOFR9854P',
  },
  bankDetails: {
    bankName: 'THE KARNAVATI CO-OP BANK LTD.',
    branchName: 'NIKOL ROAD',
    accountNo: '124003012000007',
    ifscCode: 'GSCB0UTKCBL',
  },
  notes: [
    '1 All Calculations are on the basis of an English calendar months.',
    '2 Subject to Ahmedabad Jurisdiction.',
  ],
  signatory: {
    companyName: 'For, Rajput Security Service',
    signatoryTitle: 'Authorised Signatory',
  },
  footer: {
    leftNotice: 'E & O.E.',
    disclaimer: "This is a computer-generated invoice doesn't require any signature.",
  },
  letterhead: {
    enabled: true,
    mode: 'digital',
    topMarginMm: 38,
    bottomMarginMm: 26,
    sideMarginMm: 8,
    showWatermark: true,
    opacity: 100,
  },
  tableSettings: {
    maxLines: 6,
    fillEmptyLines: false,
    density: 'auto',
  },
};

// 5. SVNIT Surat Housekeeping & Sanitation (7-Col) - MAY-26 BILL.xlsm
export const SVNIT_SURAT_CONFIG = {
  formatType: 'svnit_frequency',
  recipient: {
    toTitle: 'To,',
    designation: 'The Director,',
    companyName: 'SVNIT SURAT, National Institute of Technology (NIT)',
    addressLines: [
      'SVNIT Campus Dumas Road, SURAT,',
      'GUJARAT-395007, India',
    ],
    gstin: '24AAAJS1184P1ZP',
    workDescription:
      'Work: Housekeeping, Sanitation and disinfection services at SVNIT Campus, Surat',
  },
  invoiceMeta: {
    invoicePrefix: 'RSS',
    invoiceNo: 'RSS/000/2026-27',
    invoiceDate: '29/06/2026',
    contractNo: 'GEMC-511687719261646',
    generatedDate: '24/02/2026',
    sacCode: '9985',
    billingMonthTitle: '2026-05-01',
    billingPeriod: '01-05-2026 To 31-05-2026',
  },
  billingMonthTitle: '2026-05-01',
  items: [
    { id: 'sv-1', srNo: 1, particular: 'General Cleaning (Sweeping, Mopping, Dusting); Outdoor', quantity: 72010, unitPrice: 1, frequency: 1, workingDays: 0, totalAmount: 0 },
    { id: 'sv-2', srNo: 2, particular: 'General Cleaning (Sweeping, Mopping, Dusting);Indoor', quantity: 51787, unitPrice: 2, frequency: 1, workingDays: 31, totalAmount: 3210794 },
    { id: 'sv-3', srNo: 3, particular: 'General Cleaning (Sweeping, Mopping, Dusting); Outdoor', quantity: 13596, unitPrice: 3, frequency: 2, workingDays: 31, totalAmount: 2528856 },
    { id: 'sv-4', srNo: 4, particular: 'General Cleaning (Sweeping, Mopping, Dusting); Outdoor', quantity: 13596, unitPrice: 4, frequency: 1, workingDays: 31, totalAmount: 1685904 },
    { id: 'sv-5', srNo: 5, particular: 'General Cleaning (Sweeping, Mopping, Dusting); Outdoor', quantity: 55190, unitPrice: 5, frequency: 1, workingDays: 15, totalAmount: 4139250 },
    { id: 'sv-6', srNo: 6, particular: 'Deep Cleaning; Indoor', quantity: 56908, unitPrice: 6, frequency: 1, workingDays: 21, totalAmount: 7170408 },
    { id: 'sv-7', srNo: 7, particular: 'General Cleaning (Sweeping, Mopping, Dusting);Indoor', quantity: 30116, unitPrice: 7, frequency: 1, workingDays: 21, totalAmount: 4427052 },
    { id: 'sv-8', srNo: 8, particular: 'General Cleaning (Sweeping, Mopping, Dusting); Indoor', quantity: 20814, unitPrice: 8, frequency: 1, workingDays: 10, totalAmount: 1665120 },
    { id: 'sv-9', srNo: 9, particular: 'Deep Cleaning; Indoor', quantity: 4552, unitPrice: 9, frequency: 2, workingDays: 25, totalAmount: 2048400 },
    { id: 'sv-10', srNo: 10, particular: 'Deep Cleaning; Indoor', quantity: 4552, unitPrice: 10, frequency: 1, workingDays: 25, totalAmount: 1138000 },
    { id: 'sv-11', srNo: 11, particular: 'General Cleaning (Sweeping, Mopping, Dusting); Outdoor', quantity: 70000, unitPrice: 1, frequency: 1, workingDays: 5, totalAmount: 350000 },
    { id: 'sv-12', srNo: 12, particular: 'Deep Cleaning; Indoor', quantity: 8700, unitPrice: 2, frequency: 1, workingDays: 25, totalAmount: 435000 },
    { id: 'sv-13', srNo: 13, particular: 'Deep Cleaning; Indoor', quantity: 5287, unitPrice: 3, frequency: 1, workingDays: 21, totalAmount: 333081 },
    { id: 'sv-14', srNo: 14, particular: 'General Cleaning (Sweeping, Mopping, Dusting);Indoor', quantity: 1518, unitPrice: 4, frequency: 1, workingDays: 15, totalAmount: 91080 },
  ],
  taxScheme: 'reverse_gst_inclusive',
  reverseGstFactor: 0.847457627118644,
  companyTaxInfo: {
    gstin: '24AAOFR9854P1Z4',
    panNo: 'AAOFR9854P',
  },
  bankDetails: {
    bankName: 'The Karnavati Co-op Bank Ltd.',
    branchName: 'NIKOL ROAD, AHMEDABAD',
    accountNo: '124003005002936',
    ifscCode: 'GSCB0UTKCBL',
  },
  notes: [
    '1 Subject to Ahmedabad Jurisdiction.',
    '2 This bill is to be paid by 7th of currents months.',
    '3 All Calculations are on the basis of an English calendar months.',
  ],
  signatory: {
    companyName: 'For, Rajput Security service',
    signatoryTitle: 'Authorised Signatory',
  },
  footer: {
    leftNotice: 'E & O.E.',
    disclaimer: "This is a computer-generated invoice doesn't require any signature.",
  },
  letterhead: {
    enabled: true,
    mode: 'digital',
    topMarginMm: 38,
    bottomMarginMm: 26,
    sideMarginMm: 8,
    showWatermark: true,
    opacity: 100,
  },
  tableSettings: {
    maxLines: 14,
    fillEmptyLines: false,
    density: 'auto',
  },
};

// 5 Official Presets in memory
export const OFFICIAL_PRESETS = [
  {
    _id: 'preset-srfdcl',
    name: 'SRFDCL Riverfront Security Format',
    description: 'Official monthly security services bill format matching government / SRFDCL work orders.',
    formatType: 'standard',
    isDefault: true,
    isActive: true,
    config: DEFAULT_BILL_CONFIG,
  },
  {
    _id: 'preset-dental',
    name: 'Dental College Security (Ver 2.0)',
    description: 'Version 2.0 security service invoice format with Quantity, Present Days, and Statutory Compliance Daily Wages matching INVOICE.xlsx.',
    formatType: 'security_v2',
    isDefault: false,
    isActive: true,
    config: DENTAL_COLLEGE_CONFIG,
  },
  {
    _id: 'preset-amc-nursing',
    name: 'AMC MET Nursing College (Security & Bouncer)',
    description: 'Version 2.0 multi-guard security & bouncer format matching JULY-26.xlsx.',
    formatType: 'security_v2',
    isDefault: false,
    isActive: true,
    config: AMC_NURSING_CONFIG,
  },
  {
    _id: 'preset-baou',
    name: 'BAOU Open University Sanitation & Facility',
    description: 'Sanitation & facility management format for Dr. Babasaheb Ambedkar Open University with GeM contract details, CGST/SGST, Consumables & Machinery charges.',
    formatType: 'baou_gem',
    isDefault: false,
    isActive: true,
    config: BAOU_CONFIG,
  },
  {
    _id: 'preset-svnit',
    name: 'SVNIT Surat Housekeeping & Sanitation (7-Col)',
    description: 'Multi-cycle 7-column housekeeping format with Frequency, Working Days, and 18% Reverse GST mechanism matching MAY-26 BILL.xlsm.',
    formatType: 'svnit_frequency',
    isDefault: false,
    isActive: true,
    config: SVNIT_SURAT_CONFIG,
  },
];

export const BillProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [templates, setTemplates] = useState(OFFICIAL_PRESETS);
  const [currentTemplate, setCurrentTemplate] = useState(OFFICIAL_PRESETS[0]);
  const [savedBills, setSavedBills] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch all templates from API
  const fetchTemplates = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setIsLoading(true);
      const res = await api.get('/bill/templates');
      if (res.data?.success) {
        const fetched = res.data.templates || [];
        if (fetched.length > 0) {
          setTemplates(fetched);
          setCurrentTemplate((prev) => {
            if (prev?.isSavedBill || prev?.isImported) return prev;
            if (!prev) {
              const defaultTpl = fetched.find((t) => t.isDefault) || fetched[0];
              return JSON.parse(JSON.stringify(defaultTpl));
            }
            // If already loaded an existing template, update its reference if present
            const matched = fetched.find((t) => t._id === prev._id || t.name === prev.name);
            return matched ? JSON.parse(JSON.stringify(matched)) : prev;
          });
        }
      }
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error('Error loading bill templates:', error);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  const fetchSavedBills = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await api.get('/bill/saved');
      if (res.data?.success) {
        setSavedBills(res.data.bills || []);
      }
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error('Error loading saved bills:', error);
      }
    }
  }, [isAuthenticated]);

  const fetchSavedBill = useCallback(async (id) => {
    const res = await api.get(`/bill/saved/${id}`);
    if (!res.data?.success || !res.data.bill) {
      throw new Error(res.data?.message || 'Saved bill not found');
    }
    return res.data.bill;
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'admin') {
      fetchTemplates();
      fetchSavedBills();
      return;
    }
  }, [isAuthenticated, user?.role, fetchTemplates, fetchSavedBills]);

  // Load / Switch active template
  const loadTemplate = (template) => {
    if (!template) return;
    setCurrentTemplate(JSON.parse(JSON.stringify(template)));
    toast.success(`Loaded: ${template.name}`);
  };

  // Reset to default reference format (or specific preset)
  const resetToDefaultReference = (presetKey = 'standard') => {
    let preset = OFFICIAL_PRESETS[0];
    if (presetKey === 'security_v2' || presetKey === 'dental') preset = OFFICIAL_PRESETS[1];
    else if (presetKey === 'amc_nursing') preset = OFFICIAL_PRESETS[2];
    else if (presetKey === 'baou_gem' || presetKey === 'baou') preset = OFFICIAL_PRESETS[3];
    else if (presetKey === 'svnit_frequency' || presetKey === 'svnit') preset = OFFICIAL_PRESETS[4];

    if (!currentTemplate) {
      setCurrentTemplate(JSON.parse(JSON.stringify(preset)));
    } else {
      setCurrentTemplate((prev) => ({
        ...prev,
        formatType: preset.formatType,
        config: JSON.parse(JSON.stringify(preset.config)),
      }));
    }
    toast.success(`Applied format: ${preset.name}`);
  };

  // Reset all presets on server
  const resetAllPresetsOnServer = async () => {
    try {
      setIsLoading(true);
      const res = await api.post('/bill/templates/reset-presets');
      if (res.data?.success) {
        toast.success('All 5 official presets refreshed from reference files!');
        await fetchTemplates();
      }
    } catch (error) {
      console.error('Error resetting presets on server:', error);
      toast.error('Failed to reset presets on server');
    } finally {
      setIsLoading(false);
    }
  };

  // Import from Excel file
  const importExcelBill = async (file) => {
    try {
      setIsLoading(true);
      const arrayBuffer = await file.arrayBuffer();
      const parsed = parseUploadedExcel(arrayBuffer, file.name);

      if (!parsed || !parsed.config) {
        throw new Error('Failed to parse bill data from uploaded Excel file.');
      }

      const draft = {
        _id: null,
        name: parsed.name || file.name.replace(/\.(xlsx|xlsm|xls)$/i, ''),
        description: parsed.description || `Imported from ${file.name}`,
        formatType: parsed.formatType || parsed.config?.formatType || 'standard',
        isDefault: false,
        isActive: true,
        isImported: true,
        config: parsed.config,
      };
      setCurrentTemplate(draft);

      const saved = await saveTemplate(draft);
      if (saved) setCurrentTemplate(saved);
      toast.success(`Imported ${draft.name}`);
      return saved || draft;
    } catch (error) {
      console.error('Import excel error:', error);
      toast.error(error.message || 'Failed to import Excel bill');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Update current template configuration
  const updateConfig = (updater) => {
    setCurrentTemplate((prev) => {
      if (!prev) return prev;
      const updatedConfig =
        typeof updater === 'function' ? updater(prev.config) : { ...prev.config, ...updater };
      return { ...prev, config: updatedConfig };
    });
  };

  const saveIssuedBill = async (payload) => {
    try {
      setIsLoading(true);
      const res = await api.post('/bill/saved', payload);
      if (res.data?.success) {
        toast.success(res.data.updated ? 'Saved bill updated' : 'Bill saved');
        await fetchSavedBills();
        return res.data.bill;
      }
      return null;
    } catch (error) {
      console.error('Error saving issued bill:', error);
      toast.error(error.response?.data?.message || 'Failed to save bill');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const openSavedBill = async (id) => {
    try {
      setIsLoading(true);
      const bill = await fetchSavedBill(id);
      if (!bill.config) {
        throw new Error('This saved bill has no invoice details');
      }
      setCurrentTemplate({
        _id: `saved-${bill._id}`,
        savedBillId: bill._id,
        templateId: bill.templateId || null,
        name: bill.invoiceNo || bill.clientName,
        description: bill.clientName || '',
        formatType: bill.formatType || bill.config?.formatType || 'standard',
        isDefault: false,
        isActive: true,
        isSavedBill: true,
        config: JSON.parse(JSON.stringify(bill.config)),
      });
      toast.success(`Opened ${bill.invoiceNo}`);
      return bill;
    } catch (error) {
      console.error('Error opening saved bill:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to open saved bill');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const removeSavedBill = async (id) => {
    try {
      setIsLoading(true);
      const res = await api.delete(`/bill/saved/${id}`);
      if (res.data?.success) {
        toast.success('Saved bill removed');
        await fetchSavedBills();
      }
    } catch (error) {
      console.error('Error deleting saved bill:', error);
      toast.error(error.response?.data?.message || 'Failed to delete saved bill');
    } finally {
      setIsLoading(false);
    }
  };

  // Save current template (Create or Update)
  const saveTemplate = async (templateData) => {
    try {
      setIsLoading(true);
      const dataToSave = templateData || currentTemplate;
      if (!dataToSave) return;

      if (dataToSave._id && !String(dataToSave._id).startsWith('preset-') && !String(dataToSave._id).startsWith('saved-')) {
        // Update existing in DB
        const res = await api.put(`/bill/templates/${dataToSave._id}`, dataToSave);
        if (res.data?.success) {
          toast.success('Bill template updated successfully');
          await fetchTemplates();
          setCurrentTemplate(res.data.template);
          return res.data.template;
        }
      } else {
        // Create new in DB
        const payload = {
          name: dataToSave.name,
          description: dataToSave.description || '',
          formatType: dataToSave.formatType || dataToSave.config?.formatType || 'standard',
          isDefault: Boolean(dataToSave.isDefault),
          isActive: dataToSave.isActive !== undefined ? dataToSave.isActive : true,
          config: dataToSave.config,
        };
        const res = await api.post('/bill/templates', payload);
        if (res.data?.success) {
          toast.success('New bill template created and saved successfully');
          await fetchTemplates();
          setCurrentTemplate(res.data.template);
          return res.data.template;
        }
      }
    } catch (error) {
      console.error('Error saving bill template:', error);
      toast.error(error.response?.data?.message || 'Failed to save bill template');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Duplicate a template
  const duplicateTemplate = async (templateId) => {
    try {
      setIsLoading(true);
      const id = templateId || currentTemplate?._id;
      if (!id) return;

      if (id.startsWith('preset-')) {
        // Preset duplication: save as new template in DB
        const preset = templates.find((t) => t._id === id) || currentTemplate;
        return await saveTemplate({
          name: `${preset.name} (Copy)`,
          description: preset.description,
          formatType: preset.formatType,
          isDefault: false,
          isActive: true,
          config: JSON.parse(JSON.stringify(preset.config)),
        });
      }

      const res = await api.post(`/bill/templates/${id}/duplicate`);
      if (res.data?.success) {
        toast.success('Template duplicated successfully');
        await fetchTemplates();
        setCurrentTemplate(res.data.template);
      }
    } catch (error) {
      console.error('Error duplicating template:', error);
      toast.error('Failed to duplicate template');
    } finally {
      setIsLoading(false);
    }
  };

  // Delete a template
  const deleteTemplate = async (templateId) => {
    try {
      setIsLoading(true);
      const id = templateId || currentTemplate?._id;
      if (!id) return;

      if (id.startsWith('preset-')) {
        toast.error('Official reference preset templates cannot be deleted');
        return;
      }

      const res = await api.delete(`/bill/templates/${id}`);
      if (res.data?.success) {
        toast.success('Template deleted successfully');
        await fetchTemplates();
        setCurrentTemplate(OFFICIAL_PRESETS[0]);
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error(error.response?.data?.message || 'Failed to delete template');
    } finally {
      setIsLoading(false);
    }
  };

  // Set default template
  const setDefaultTemplate = async (templateId) => {
    try {
      setIsLoading(true);
      const id = templateId || currentTemplate?._id;
      if (!id) return;

      if (!id.startsWith('preset-')) {
        const res = await api.patch(`/bill/templates/${id}/set-default`);
        if (res.data?.success) {
          toast.success('Set as default bill template');
          await fetchTemplates();
        }
      } else {
        toast.success(`Set "${currentTemplate.name}" as active default`);
      }
      setCurrentTemplate((prev) => ({ ...prev, isDefault: true }));
    } catch (error) {
      console.error('Error setting default template:', error);
      toast.error('Failed to set default template');
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle active status
  const toggleActiveTemplate = async (templateId) => {
    try {
      const id = templateId || currentTemplate?._id;
      if (!id || id.startsWith('preset-')) return;

      const res = await api.patch(`/bill/templates/${id}/toggle-active`);
      if (res.data?.success) {
        toast.success(res.data.message || 'Status updated');
        await fetchTemplates();
        if (currentTemplate?._id === id) {
          setCurrentTemplate((prev) => ({ ...prev, isActive: res.data.template.isActive }));
        }
      }
    } catch (error) {
      console.error('Error toggling template status:', error);
      toast.error('Failed to toggle status');
    }
  };

  return (
    <BillContext.Provider
      value={{
        templates,
        currentTemplate,
        setCurrentTemplate,
        savedBills,
        isLoading,
        fetchTemplates,
        fetchSavedBills,
        fetchSavedBill,
        saveIssuedBill,
        openSavedBill,
        removeSavedBill,
        loadTemplate,
        updateConfig,
        saveTemplate,
        duplicateTemplate,
        deleteTemplate,
        setDefaultTemplate,
        toggleActiveTemplate,
        resetToDefaultReference,
        resetAllPresetsOnServer,
        importExcelBill,
      }}
    >
      {children}
    </BillContext.Provider>
  );
};

export const useBill = () => {
  const context = useContext(BillContext);
  if (!context) {
    throw new Error('useBill must be used within a BillProvider');
  }
  return context;
};

export default BillContext;
